import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';
import { usePreviewToken } from '@/lib/queries';
import { Smartphone } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

// PREVIEW ON DEVICE (B6), IN TWO FLAVOURS.
//
//   In app  — `personalizehq-preview://phq-preview?t=…`, handed to `PHQ.handleDeepLink()`. The real
//             thing: the Swift decoder, the real surfaces, a sheet you can drag. As of P5.3/P5.4 this
//             WORKS — the SDK registers the `personalizehq-preview` scheme, `handleDeepLink()` parses
//             the token, and `examples/ios-demo` is a host build that does both. Scan and it opens.
//
//   Browser — `/preview/{token}` on this dashboard. Works on any phone, today, with no app at all,
//             and mounts THE PRODUCTION RENDERER — so the wrap points and rects are the real ones,
//             at the phone's real width and real font scale.
//
// The scheme defaults to `personalizehq-preview` — the fixed scheme the SDK's preview path checks
// (PHQ.swift), not an arbitrary per-app one — so scanning opens any app that embeds the SDK. It stays
// editable for a host that registers a different scheme.
const SCHEME_KEY = 'phq.preview_scheme';

/** The scheme the SDK's preview deep-link handler matches (PHQ.swift: `url.scheme == "personalizehq-preview"`). */
const DEFAULT_SCHEME = 'personalizehq-preview';

type Target = 'browser' | 'app';

/** THE PHONE IS NOT THIS MACHINE. `localhost` on a phone is the phone — so a QR encoding it points
 *  the device at itself and fails in a way that looks like a broken link. Dev servers bind to the
 *  LAN, so the fix is to encode the address the author reached the dashboard on, and to SAY SO when
 *  that address cannot possibly work from another device. */
const previewOrigin = (): string => window.location.origin;
const isLoopback = (origin: string): boolean => /\/\/(localhost|127\.0\.0\.1|\[::1\])(:|$)/.test(origin);

export function PreviewDialog({
  experienceId,
  variantKey,
  variantName,
}: {
  experienceId: string;
  /** The builder's ACTIVE experiment arm (ADR 0015): the browser preview renders it. Null = original. */
  variantKey?: string | null;
  variantName?: string | null;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Target>('browser');
  const [scheme, setScheme] = useState(() => localStorage.getItem(SCHEME_KEY) || DEFAULT_SCHEME);
  // Keyed by the url it encodes, so a QR for a stale token can never be shown next to a fresh one —
  // and so the effect below never has to setState synchronously just to clear it.
  const [qr, setQr] = useState<{ url: string; data: string } | null>(null);
  const mint = usePreviewToken(experienceId);

  const { mutate, reset } = mint;
  useEffect(() => {
    // A token is minted per OPEN, not per render: 15 minutes is plenty for a scan, and a token the
    // dialog holds forever is a draft link that outlives the session that made it.
    if (open) mutate();
    else reset();
  }, [open, mutate, reset]);

  const token = mint.data?.token;
  const origin = previewOrigin();
  // The browser flavour previews the ACTIVE variant. The in-app deep link deliberately does not:
  // its `?t=` shape is parsed by a pinned SDK decoder (PHQ.handleDeepLink), so it always shows the
  // original — said in the UI below rather than silently.
  const variantQuery = variantKey ? `?variant=${encodeURIComponent(variantKey)}` : '';
  const url =
    !token ? null : target === 'browser' ? `${origin}/preview/${token}${variantQuery}` : scheme ? `${scheme}://phq-preview?t=${token}` : null;

  useEffect(() => {
    if (!url) return;
    let live = true;
    // `toDataURL` is async and the dialog can close under it — a late setState on a closed dialog is
    // how a stale QR shows up next time it opens.
    void QRCode.toDataURL(url, { margin: 1, width: 320 }).then((data) => {
      if (live) setQr({ url, data });
    });
    return () => {
      live = false;
    };
  }, [url]);

  const image = qr?.url === url ? qr.data : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="shadow-none">
          <Smartphone className="size-4" />
          Preview
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle>Preview on device</DialogTitle>
          <DialogDescription>
            Scan with your phone. It renders this draft as it is right now — saved or not published —
            and expires in 15 minutes.
          </DialogDescription>
        </DialogHeader>

        <div role="radiogroup" aria-label="preview target" className="flex gap-0.5 rounded-md border p-0.5">
          {(
            [
              { value: 'browser', label: 'In a browser' },
              { value: 'app', label: 'In your app' },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={target === option.value}
              aria-label={option.label}
              onClick={() => setTarget(option.value)}
              className={cn(
                'flex-1 rounded px-2 py-1 text-[12px]',
                target === option.value ? 'bg-indigo-500/20 text-indigo-400' : 'text-muted-foreground'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {target === 'app' ? (
          <div className="space-y-2">
            <Label htmlFor="preview-scheme">Your app’s URL scheme</Label>
            <Input
              id="preview-scheme"
              value={scheme}
              placeholder="myapp"
              spellCheck={false}
              onChange={(e) => {
                // Paste-proof: an author who types `myapp://` has done nothing wrong.
                const next = e.target.value.replace(/[^a-zA-Z0-9.+-]/g, '').toLowerCase();
                setScheme(next);
                localStorage.setItem(SCHEME_KEY, next);
              }}
            />
            {/* The SDK registers `personalizehq-preview` and handles the link (PHQ.handleDeepLink). A
                phone WITHOUT such an app installed will say "no usable data found" — that is the only
                failure mode now, and it means the app isn't on the device, not that the flow is unbuilt. */}
            <p className="text-[11px] text-muted-foreground">
              Opens any app embedding the SDK (it registers this scheme and calls{' '}
              <code>PHQ.handleDeepLink()</code>). The <code>examples/ios-demo</code> app does. If no such
              app is installed, the camera says “no usable data found” — use <b>In a browser</b> instead.
            </p>
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            Opens in the phone’s browser and runs the <b>production renderer</b> — the same one the SDK
            is conformance-tested against — at your phone’s real width and font scale. No app needed.
            {variantKey ? (
              <>
                {' '}Shows the <b>{variantName ?? variantKey}</b> variant.
              </>
            ) : null}
          </p>
        )}

        {target === 'app' && variantKey ? (
          <p className="text-[11px] text-amber-500">
            In-app preview shows the original — the deep link a shipped SDK parses carries only the
            token. Use <b>In a browser</b> to see “{variantName ?? variantKey}”.
          </p>
        ) : null}

        <div className="grid min-h-[240px] place-items-center rounded-lg border bg-white p-4">
          {mint.isError ? (
            <p className="text-center text-sm text-destructive">{mint.error.message}</p>
          ) : target === 'app' && !scheme ? (
            <p className="max-w-[220px] text-center text-sm text-zinc-500">
              Enter the scheme your app registers (the one in <code>CFBundleURLSchemes</code>) to get a
              QR code.
            </p>
          ) : image ? (
            <img src={image} alt={`QR code for ${url}`} className="size-[240px]" />
          ) : (
            <p className="text-sm text-zinc-500">Minting a preview link…</p>
          )}
        </div>

        {/* THE OTHER WAY THIS WASTES AN AFTERNOON. `localhost` on a phone IS the phone: a QR encoding
            it points the device at itself, gets connection-refused, and looks exactly like a broken
            link. Vite already serves on the LAN — it just has to be REACHED there. */}
        {target === 'browser' && isLoopback(origin) ? (
          <p className="text-[11px] text-amber-500">
            This QR points at <code>{origin}</code> — on a phone, that is the <i>phone</i>. Open the
            dashboard on your machine’s LAN address (<code>pnpm dev --host</code>, then use the
            Network URL it prints) and re-open this dialog.
          </p>
        ) : null}

        {url && <p className="break-all text-center font-mono text-[11px] text-muted-foreground">{url}</p>}
      </DialogContent>
    </Dialog>
  );
}
